import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/ui/dashboard-layout';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { KanbanBoard } from '@/components/kanban/kanban-board';
import { TaskDialog } from '@/components/tasks/task-dialog';
import { ProjectDialog } from '@/components/projects/project-dialog';
import { ProjectActivity } from '@/components/projects/project-activity';
import { GanttChart } from '@/components/projects/gantt-chart';
import { SiteHealthTab } from '@/components/projects/site-health-tab';
import { ProjectOverviewTab } from '@/components/projects/project-overview-tab';
import { ProjectFilesTab } from '@/components/projects/project-files-tab';
import { ProjectTicketsTab } from '@/components/projects/project-tickets-tab';
import { ProjectQuotesTab } from '@/components/projects/project-quotes-tab';
import { ProjectInvoicesTab } from '@/components/projects/project-invoices-tab';
import { useProject, useUpdateProjectStage, useCloneProject, useDeleteProject } from '@/hooks/use-projects';
import { useInvoices } from '@/hooks/use-invoices';
import { useQuotes } from '@/hooks/use-quotes';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { useTasks } from '@/hooks/use-tasks';
import { useTickets } from '@/hooks/use-tickets';
import { useFiles } from '@/hooks/use-files';
import { useAuthStore } from '@/stores/auth-store';
import { toast } from '@/hooks/use-toast';
import { useProjectRoom } from '@/hooks/use-socket';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Loader2,
  Calendar,
  Target,
  Users,
  FileText,
  Ticket,
  PoundSterling,
  CheckSquare,
  Pencil,
  MessageSquare,
  Plus,
  Receipt,
  Columns3,
  GitBranch,
  HeartPulse,
  FileSpreadsheet,
  Copy,
  Info,
  Trash2,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn, formatDate, formatCurrency } from '@/lib/utils';

const stageColors: Record<string, string> = {
  enquiry: 'bg-purple-500/10 text-purple-400',
  quote: 'bg-blue-500/10 text-blue-400',
  design: 'bg-yellow-500/10 text-yellow-400',
  develop: 'bg-orange-500/10 text-orange-400',
  review: 'bg-pink-500/10 text-pink-400',
  launch: 'bg-cyan-500/10 text-cyan-400',
  finalised: 'bg-dyndev-green/10 text-dyndev-green',
};

const ITEMS_PER_PAGE = 10;

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // ── Shared dialog state ──────────────────────────────────────
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);

  // ── Stage management ─────────────────────────────────────────
  const [selectedStage, setSelectedStage] = useState('');
  const updateStage = useUpdateProjectStage();
  const cloneProject = useCloneProject();
  const deleteProject = useDeleteProject();
  const [deleteOpen, setDeleteOpen] = useState(false);

  // ── Tab & pagination state ───────────────────────────────────
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') || 'overview'
  );
  const [ticketPage, setTicketPage] = useState(1);
  const [invoicePage, setInvoicePage] = useState(1);
  const [filesPage, setFilesPage] = useState(1);

  // ── Real-time socket ─────────────────────────────────────────
  useProjectRoom(id);

  // ── Data fetching ────────────────────────────────────────────
  const {
    data: projectData,
    isLoading: projectLoading,
    isError: projectError,
    refetch: refetchProject,
  } = useProject(id!);

  const projectTitle = projectData?.data?.name || 'Project';
  useDocumentTitle(projectTitle);

  const { data: tasksData, isLoading: tasksLoading } = useTasks(
    id ? { projectId: id } : undefined
  );
  const { data: ticketsData, isLoading: ticketsLoading } = useTickets(
    id
      ? {
          projectId: id,
          page: String(ticketPage),
          limit: String(ITEMS_PER_PAGE),
        }
      : undefined
  );
  const { data: invoicesData, isLoading: invoicesLoading } =
    useInvoices(
      id
        ? {
            projectId: id,
            page: String(invoicePage),
            limit: String(ITEMS_PER_PAGE),
          }
        : undefined
    );
  const { data: filesData, isLoading: filesLoading } = useFiles(
    id
      ? {
          projectId: id,
          page: String(filesPage),
          limit: String(ITEMS_PER_PAGE),
        }
      : undefined
  );
  const { data: quotesData, isLoading: quotesLoading } = useQuotes(
    id ? { projectId: id } : undefined
  );

  const project = projectData?.data;
  const tasks = tasksData?.data || [];
  const projectTickets = ticketsData?.data || [];
  const projectInvoices = invoicesData?.data || [];
  const projectFiles = filesData?.data || [];
  const projectQuotes = quotesData?.data || [];
  const ticketPagination = (
    ticketsData as {
      pagination?: { totalPages: number; total: number };
    }
  )?.pagination;
  const invoicePagination = (
    invoicesData as {
      pagination?: { totalPages: number; total: number };
    }
  )?.pagination;
  const filePagination = (
    filesData as {
      pagination?: { totalPages: number; total: number };
    }
  )?.pagination;

  // ── Sync stage + highlight task ──────────────────────────────
  useEffect(() => {
    if (project) setSelectedStage(project.stage);
  }, [project?.id]);

  const [highlightTaskId, setHighlightTaskId] = useState<string | null>(
    null
  );
  useEffect(() => {
    const taskId = searchParams.get('taskId');
    if (taskId) setHighlightTaskId(taskId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const taskIdParam = searchParams.get('taskId');
  useEffect(() => {
    if (
      taskIdParam &&
      tasks.length > 0 &&
      activeTab === 'kanban' &&
      !taskDialogOpen
    ) {
      const task = tasks.find((t: any) => t.id === taskIdParam);
      if (task) {
        setSelectedTask(task);
        setTaskDialogOpen(true);
        const params = new URLSearchParams(searchParams);
        params.delete('taskId');
        navigate(`/projects/${id}?${params.toString()}`, {
          replace: true,
        });
      }
    }
  }, [
    taskIdParam,
    tasks,
    activeTab,
    taskDialogOpen,
    id,
    navigate,
    searchParams,
  ]);

  useEffect(() => {
    if (!taskDialogOpen) setHighlightTaskId(null);
  }, [taskDialogOpen]);

  // ── Derived data ─────────────────────────────────────────────
  const isClient = user?.role === 'client';
  const isAdmin = user?.role === 'admin';
  const stageOrder = [
    'enquiry',
    'quote',
    'design',
    'develop',
    'review',
    'launch',
    'finalised',
  ];
  const stageIndex = stageOrder.indexOf(project?.stage);
  const totalStageCount = 7;
  const progress = project
    ? stageIndex >= 0
      ? Math.round(((stageIndex + 1) / totalStageCount) * 100)
      : 0
    : 0;

  const currentIdx = stageOrder.indexOf(project?.stage ?? 'enquiry');
  const derivedStages = stageOrder.map((name, idx) => {
    if (idx < currentIdx)
      return { name, status: 'completed' };
    if (idx === currentIdx)
      return {
        name,
        status:
          project?.stage === 'finalised'
            ? 'completed'
            : 'in_progress',
      };
    return { name, status: 'not_started' };
  });

  // ── Error / loading / not-found states ───────────────────────
  if (projectError) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <p className="text-red-400 mb-4">
            Failed to load project.
          </p>
          <button
            onClick={() => refetchProject()}
            className="btn-dyndev text-sm"
          >
            Try Again
          </button>
        </div>
      </DashboardLayout>
    );
  }

  if (projectLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-6 animate-pulse">
          <div className="h-4 w-32 rounded bg-[var(--border-color)]" />
          <div className="h-8 w-64 rounded bg-[var(--border-color)]" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-16 rounded-lg bg-[var(--border-color)]"
              />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <h2 className="text-xl font-bold mb-2">
            Project not found
          </h2>
          <button
            onClick={() => navigate('/projects')}
            className="text-dyndev-green hover:underline"
          >
            Back to projects
          </button>
        </div>
      </DashboardLayout>
    );
  }

  // ── Main render ──────────────────────────────────────────────
  return (
    <DashboardLayout>
      {/* Back button */}
      <button
        onClick={() =>
          navigate(isClient ? '/' : '/projects')
        }
        className="flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-4 transition-colors"
      >
        <ArrowLeft size={16} />
        {isClient ? 'Back to Dashboard' : 'Back to Projects'}
      </button>

      {/* Project header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{project.name}</h1>
          <Badge
            variant="outline"
            className={cn(
              'text-xs',
              stageColors[project.stage] ||
                'bg-gray-500/10 text-gray-400'
            )}
          >
            {project.stage}
          </Badge>
          {!isClient && (
            <button
              onClick={() => setProjectDialogOpen(true)}
              className="text-[var(--text-secondary)] hover:text-dyndev-green transition-colors"
              title="Edit project"
            >
              <Pencil size={14} />
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setDeleteOpen(true)}
              className="text-[var(--text-secondary)] hover:text-red-400 transition-colors"
              title="Delete project"
              aria-label="Delete project"
            >
              <Trash2 size={14} />
            </button>
          )}
          {!isClient && (
            <button
              onClick={async () => {
                try {
                  const result =
                    await cloneProject.mutateAsync(
                      project.id
                    );
                  toast({
                    title: 'Project cloned',
                    description: `"${result.data?.name || 'Copy'}" created.`,
                    variant: 'success',
                  });
                  if (result.data?.id)
                    navigate(
                      `/projects/${result.data.id}`
                    );
                } catch (err: any) {
                  toast({
                    title: 'Clone failed',
                    description:
                      err?.message ||
                      'An unexpected error occurred.',
                    variant: 'destructive',
                  });
                }
              }}
              disabled={cloneProject.isPending}
              className="text-[var(--text-secondary)] hover:text-dyndev-green transition-colors"
              title="Clone project"
            >
              {cloneProject.isPending ? (
                <Loader2
                  size={14}
                  className="animate-spin"
                />
              ) : (
                <Copy size={14} />
              )}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isClient ? (
            <button
              onClick={() => {
                setSelectedTask(null);
                setTaskDialogOpen(true);
              }}
              className="btn-dyndev text-sm"
            >
              + Add Task
            </button>
          ) : (
            <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
              <div className="w-24 h-2 rounded-full bg-[var(--border-color)] overflow-hidden">
                <div
                  className="h-full rounded-full bg-dyndev-green transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-xs font-medium">
                {progress}% complete
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Project meta info */}
      {project.description && (
        <p className="text-sm text-[var(--text-secondary)] mb-6">
          {project.description}
        </p>
      )}

      {/* Meta grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <div className="glass rounded-lg p-3">
          <div className="flex items-center gap-2 text-[var(--text-secondary)] text-xs mb-1">
            <Calendar size={12} /> Start Date
          </div>
          <p className="text-sm font-medium">
            {project.startDate
              ? formatDate(project.startDate)
              : 'TBD'}
          </p>
        </div>
        <div className="glass rounded-lg p-3">
          <div className="flex items-center gap-2 text-[var(--text-secondary)] text-xs mb-1">
            <Target size={12} /> Target Date
          </div>
          <p className="text-sm font-medium">
            {project.targetDate
              ? formatDate(project.targetDate)
              : 'TBD'}
          </p>
        </div>
        <div className="glass rounded-lg p-3">
          <div className="flex items-center gap-2 text-[var(--text-secondary)] text-xs mb-1">
            <Users size={12} /> PM
          </div>
          <p className="text-sm font-medium">
            {project.projectManager?.name || 'Unassigned'}
          </p>
        </div>
        <div className="glass rounded-lg p-3">
          <div className="flex items-center gap-2 text-[var(--text-secondary)] text-xs mb-1">
            <PoundSterling size={12} /> Budget
          </div>
          <p className="text-sm font-medium">
            {project.budget
              ? formatCurrency(project.budget)
              : 'TBD'}
          </p>
        </div>
        <div className="glass rounded-lg p-3">
          <div className="flex items-center gap-2 text-[var(--text-secondary)] text-xs mb-1">
            <CheckSquare size={12} /> Tasks
          </div>
          <p className="text-sm font-medium">
            {project._count?.tasks || 0}
          </p>
        </div>
        <div className="glass rounded-lg p-3">
          <div className="flex items-center gap-2 text-[var(--text-secondary)] text-xs mb-1">
            <FileText size={12} /> Files
          </div>
          <p className="text-sm font-medium">
            {project._count?.files || 0}
          </p>
        </div>
      </div>

      {/* Stage updater for non-clients */}
      {!isClient && (
        <div className="flex items-center gap-3 mb-6 p-3 rounded-lg bg-[var(--bg-secondary)]">
          <span className="text-sm text-[var(--text-secondary)]">
            Stage:
          </span>
          <Select
            value={selectedStage}
            onValueChange={setSelectedStage}
          >
            <SelectTrigger
              className="w-[160px]"
              aria-label="Select project stage"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="enquiry">Enquiry</SelectItem>
              <SelectItem value="quote">Quote</SelectItem>
              <SelectItem value="design">Design</SelectItem>
              <SelectItem value="develop">Develop</SelectItem>
              <SelectItem value="review">Review</SelectItem>
              <SelectItem value="launch">Launch</SelectItem>
              <SelectItem value="finalised">
                Finalised
              </SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            disabled={
              selectedStage === project.stage ||
              updateStage.isPending
            }
            onClick={async () => {
              try {
                await updateStage.mutateAsync({
                  id: project.id,
                  stage: selectedStage,
                });
                toast({
                  title: 'Stage updated',
                  description: `Project moved to ${selectedStage.charAt(0).toUpperCase() + selectedStage.slice(1)}.`,
                  variant: 'success',
                });
              } catch (err: any) {
                toast({
                  title: 'Failed to update stage',
                  description:
                    err?.message ||
                    'An unexpected error occurred.',
                  variant: 'destructive',
                });
              }
            }}
          >
            {updateStage.isPending && (
              <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
            )}
            Save
          </Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="overview">
            <Info size={14} className="mr-1.5" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="kanban">
            <Columns3 size={14} className="mr-1.5" />
            Kanban
          </TabsTrigger>
          <TabsTrigger value="gantt">
            <GitBranch size={14} className="mr-1.5" />
            Gantt
          </TabsTrigger>
          <TabsTrigger value="files">
            <FileText size={14} className="mr-1.5" />
            Files
          </TabsTrigger>
          <TabsTrigger value="tickets">
            <Ticket size={14} className="mr-1.5" />
            Tickets
          </TabsTrigger>
          <TabsTrigger value="quotes">
            <FileSpreadsheet size={14} className="mr-1.5" />
            Quotes
          </TabsTrigger>
          <TabsTrigger value="invoices">
            <Receipt size={14} className="mr-1.5" />
            Invoices
          </TabsTrigger>
          <TabsTrigger value="activity">
            <MessageSquare size={14} className="mr-1.5" />
            Activity
          </TabsTrigger>
          {project.siteMonitor && (
            <TabsTrigger value="health">
              <HeartPulse size={14} className="mr-1.5" />
              Site Health
            </TabsTrigger>
          )}
        </TabsList>

        {/* Overview Tab — extracted */}
        <TabsContent value="overview">
          <ProjectOverviewTab
            project={project}
            isClient={isClient}
            derivedStages={derivedStages}
          />
        </TabsContent>

        {/* Kanban Tab */}
        <TabsContent value="kanban">
          <KanbanBoard
            tasks={tasks}
            isLoading={tasksLoading}
            highlightedTaskId={highlightTaskId}
            onTaskClick={(task) => {
              setSelectedTask(task);
              setTaskDialogOpen(true);
            }}
            onTaskCreate={() => {
              setSelectedTask(null);
              setTaskDialogOpen(true);
            }}
          />
        </TabsContent>

        {/* Gantt Tab */}
        <TabsContent value="gantt" className="space-y-4">
          <GanttChart
            tasks={tasks.map((t: any) => ({
              id: t.id,
              title: t.title,
              dueDate: t.dueDate,
              startDate: t.startDate,
              estimatedHours: t.estimatedHours,
              status: t.status,
              priority: t.priority,
              parentId: t.parentId,
              assignee: t.assignee || null,
              projectId: t.projectId || id!,
              projectName: project.name,
            }))}
            milestones={project.milestones || []}
            projectStartDate={project.startDate}
            projectTargetDate={project.targetDate}
          />
        </TabsContent>

        {/* Files Tab — extracted */}
        <TabsContent value="files">
          <ProjectFilesTab
            projectId={id!}
            isClient={isClient}
            files={projectFiles}
            filesLoading={filesLoading}
            page={filesPage}
            onPageChange={setFilesPage}
            pagination={filePagination ?? null}
          />
        </TabsContent>

        {/* Tickets Tab — extracted */}
        <TabsContent value="tickets">
          <ProjectTicketsTab
            projectId={id!}
            tickets={projectTickets}
            ticketsLoading={ticketsLoading}
            page={ticketPage}
            onPageChange={setTicketPage}
            pagination={ticketPagination ?? null}
          />
        </TabsContent>

        {/* Quotes Tab — extracted */}
        <TabsContent value="quotes">
          <ProjectQuotesTab
            quotes={projectQuotes}
            quotesLoading={quotesLoading}
          />
        </TabsContent>

        {/* Invoices Tab — extracted */}
        <TabsContent value="invoices">
          <ProjectInvoicesTab
            isClient={isClient}
            invoices={projectInvoices}
            invoicesLoading={invoicesLoading}
            page={invoicePage}
            onPageChange={setInvoicePage}
            pagination={invoicePagination ?? null}
          />
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-4">
          <ProjectActivity projectId={id!} />
        </TabsContent>

        {/* Site Health Tab */}
        <TabsContent value="health" className="space-y-4">
          <SiteHealthTab projectId={id!} isAdmin={!isClient} />
        </TabsContent>
      </Tabs>

      {/* Shared dialogs (used across tabs) */}
      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        projectId={id}
        task={selectedTask}
      />

      {/* Project dialog — staff only (edit button is hidden for clients) */}
      {!isClient && (
        <ProjectDialog
          open={projectDialogOpen}
          onOpenChange={setProjectDialogOpen}
          project={project}
        />
      )}

      {/* Delete confirmation — admin only */}
      <AlertDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete project?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{project.name}"?
              This will permanently remove the project, its tasks, files, tickets,
              milestones, comments and site monitor. Any invoices linked to this
              project will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await deleteProject.mutateAsync(project.id);
                  toast({
                    title: 'Project deleted',
                    description: `"${project.name}" has been deleted.`,
                    variant: 'success',
                  });
                  navigate('/projects');
                } catch (err: any) {
                  toast({
                    title: 'Failed to delete project',
                    description:
                      err?.message || 'An unexpected error occurred.',
                    variant: 'destructive',
                  });
                }
              }}
              disabled={deleteProject.isPending}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleteProject.isPending
                ? 'Deleting...'
                : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
